import { type FC, useEffect, useState, type ComponentProps } from 'react';

import AddressFinder, {
  defaultValues as addressFinderDefaultValues,
  addressFinderSchema,
} from '@eneco/components/AddressFinder/AddressFinder';
import RichText from '@eneco/components/RichText/RichText';
import { TrackedNotificationBox } from '@eneco/components/TrackedNotificationBox/TrackedNotificationBox';
import { ApiError } from '@eneco/dc/client/ApiError';
import { typedYupResolver } from '@eneco/hooks/form';
import { NotificationBox, Stack } from '@eneco/sparky';
import { Customers_Address_AddressModel } from '@monorepo-types/dc';
import { useMachine } from '@xstate/react';
import { FormProvider, useForm } from 'react-hook-form';
import * as yup from 'yup';

import { Header, Layout, NextButton } from '../../components';
import { addressAvailabilityMachine } from '../../machines/addressAvailabilityMachine';
import { FlowContext } from '../../types';
import { useFlowHooks } from '../../utils/FlowProvider';

interface FormFields {
  postalCode: string;
  houseNumber: number;
  houseNumberSuffix?: string;
  city: string;
  street: string;
}

type AddressFinderLabels = NonNullable<ComponentProps<typeof AddressFinder>['customLabels']>;

export interface AddressFinderAvailabilityStepFields {
  address?: {
    houseNumberFormField?: AddressFinderLabels['houseNumber'];
    houseNumberSuffixFormField?: AddressFinderLabels['houseNumberSuffix'];
    postalCodeFormField?: AddressFinderLabels['postalCode'];
  };
  content?: {
    nextStepText?: { value?: string };
    notification?: {
      value?: {
        content?: string;
        title?: string;
        variant?: 'info' | 'success' | 'warning' | 'error' | 'chat';
      };
    };
    text?: { value?: string };
    title?: { value?: string };
  };
}

interface Props {
  product: string;
  orderType: string;
  textualData?: AddressFinderAvailabilityStepFields;
  /** When true, unavailable addresses use TrackedNotificationBox instead of NotificationBox. */
  trackUnavailableNotification?: boolean;
}

export const AddressFinderAvailabilityStep: FC<Props> = ({
  product,
  orderType,
  textualData,
  trackUnavailableNotification = false,
}) => {
  const { useFlowActorRef, useFlowSelector } = useFlowHooks<FlowContext>();
  const { send: sendToFlowMachine } = useFlowActorRef();
  const [isSuffixRequired, setIsSuffixRequired] = useState(false);
  const [errorData, setErrorData] = useState<ApiError>();
  const [isLoading, setIsLoading] = useState(false);
  const [addressFinderData, setAddressFinderData] = useState<Customers_Address_AddressModel>();

  const postalCode = useFlowSelector(state => state.context.postalCode);
  const houseNumber = useFlowSelector(state => state.context.houseNumber);
  const houseNumberSuffix = useFlowSelector(state => state.context.houseNumberSuffix);
  const street = useFlowSelector(state => state.context.street);
  const city = useFlowSelector(state => state.context.city);

  const [availabilityState, sendToAvailabilityMachine] = useMachine(addressAvailabilityMachine);

  const formSchema = yup.object({
    ...addressFinderSchema(isSuffixRequired).fields,
  });

  const formMethods = useForm<FormFields>({
    mode: 'onBlur',
    defaultValues: {
      ...addressFinderDefaultValues,
      postalCode,
      houseNumber,
      houseNumberSuffix: houseNumberSuffix ?? undefined,
      street,
      city,
    },
    resolver: typedYupResolver<FormFields>(formSchema),
  });

  const {
    handleSubmit,
    getValues,
    formState: { isSubmitSuccessful },
  } = formMethods;

  useEffect(() => {
    if (addressFinderData?.postalCode && addressFinderData?.houseNumber) {
      sendToAvailabilityMachine({
        type: 'CHECK_ADDRESS',
        value: addressFinderData.postalCode.substring(0, 2),
        product,
        orderType,
      });
    }
  }, [addressFinderData?.postalCode, addressFinderData?.houseNumber, orderType, product, sendToAvailabilityMachine]);

  useEffect(() => {
    if (isSubmitSuccessful && !errorData && addressFinderData && availabilityState.context?.isAvailable) {
      const formData = getValues();

      sendToFlowMachine({
        type: 'NEXT_PAGE',
        values: {
          ...addressFinderData,
          ...formData,
          postalCode: addressFinderData?.postalCode?.toString() ?? formData.postalCode.replace(/\s+/g, ''),
        },
      });
    }
  }, [
    availabilityState.context?.isAvailable,
    isSubmitSuccessful,
    addressFinderData,
    errorData,
    getValues,
    sendToFlowMachine,
  ]);

  const renderButtonAndAvailabilityNotification = () => {
    if (availabilityState.context?.isAvailable === false) {
      const notificationProps = {
        isAlert: false,
        variant: textualData?.content?.notification?.value?.variant,
        title: textualData?.content?.notification?.value?.title ?? '',
        text: <RichText html={textualData?.content?.notification?.value?.content} />,
      };

      return trackUnavailableNotification ? (
        <TrackedNotificationBox {...notificationProps} />
      ) : (
        <NotificationBox {...notificationProps} />
      );
    }

    return (
      <NextButton isLoading={isSubmitSuccessful || isLoading}>{textualData?.content?.nextStepText?.value}</NextButton>
    );
  };

  return (
    <FormProvider {...formMethods}>
      <Layout.Content variant="A" handleSubmit={handleSubmit}>
        <Header>
          <Header.Title>{textualData?.content?.title?.value}</Header.Title>
          {textualData?.content?.text?.value && <Header.Text>{textualData.content.text.value}</Header.Text>}
        </Header>

        <Stack.Item as="fieldset">
          <AddressFinder
            isSuffixRequired={isSuffixRequired}
            setIsSuffixRequired={setIsSuffixRequired}
            customLabels={{
              houseNumber: textualData?.address?.houseNumberFormField,
              houseNumberSuffix: textualData?.address?.houseNumberSuffixFormField,
              postalCode: textualData?.address?.postalCodeFormField,
            }}
            setErrorData={setErrorData}
            setIsLoading={setIsLoading}
            setAddressFinderData={setAddressFinderData}
          />
        </Stack.Item>
        {renderButtonAndAvailabilityNotification()}
      </Layout.Content>
    </FormProvider>
  );
};
